/**
 * LifeAdvice Overview — the section's landing page.
 *
 * One fetch fan-out (summary + entries-over-time + category distribution) over
 * a fixed 30-day window, rendered as a MetricCard row, two compact recharts,
 * and navigation cards into the other five pages. Local useState/useEffect
 * against lifeAdviceAdminService — no Redux, no react-query (matching the
 * newest pages in this app).
 */

import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Card,
  CardActionArea,
  CardContent,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

import MetricCard from '../../common/MetricCard';
import EmptyState from '../../common/EmptyState';
import { brandColors } from '../../../theme/brandColors';
import {
  lifeAdviceApi,
  AnalyticsSummary,
  EntriesOverTimePoint,
  CategoryDistributionRow,
} from '../../../services/lifeAdviceAdminService';
import { PageHeader, errorMessage, isBackendDown, formatDate } from '../common/lifeAdviceUi';

const WINDOW_DAYS = 30;

const NAV_CARDS: Array<{ path: string; label: string; blurb: string }> = [
  {
    path: 'pillars',
    label: 'Wisdom Pillars',
    blurb: 'The categories members choose from — colours, icons, order, and status.',
  },
  {
    path: 'prompts',
    label: 'Prompts',
    blurb: 'The questions that start an entry. Create, edit, star, or bulk-import by CSV.',
  },
  {
    path: 'curated',
    label: 'Curated Library',
    blurb: 'LifeScribe-written wisdom offered when a member would rather read than record.',
  },
  {
    path: 'entries',
    label: 'Member Entries',
    blurb: 'Read-only view of what members have recorded, transformed, and shared.',
  },
  {
    path: 'analytics',
    label: 'Analytics',
    blurb: 'Trends, category mix, share methods, top prompts, and the creation funnel.',
  },
];

const LifeAdviceOverviewPage: React.FC = () => {
  const navigate = useNavigate();

  const [summary, setSummary] = React.useState<AnalyticsSummary | null>(null);
  const [timeseries, setTimeseries] = React.useState<EntriesOverTimePoint[]>([]);
  const [distribution, setDistribution] = React.useState<CategoryDistributionRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [down, setDown] = React.useState(false);

  const reload = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    setDown(false);
    try {
      const [s, t, d] = await Promise.all([
        lifeAdviceApi.analytics.summary(WINDOW_DAYS),
        lifeAdviceApi.analytics.entriesOverTime(WINDOW_DAYS),
        lifeAdviceApi.analytics.categoryDistribution(WINDOW_DAYS),
      ]);
      setSummary(s);
      setTimeseries(t);
      setDistribution(d);
    } catch (e) {
      setDown(isBackendDown(e));
      setError(errorMessage(e, 'Failed to load the LifeAdvice overview.'));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    reload();
  }, [reload]);

  if (loading && !summary) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (down) {
    return (
      <EmptyState
        icon="error"
        title="LifeAdvice service is unreachable"
        description={`The admin API could not reach lifescribe-advice. ${error || ''}`}
        action={{ label: 'Retry', onClick: reload, variant: 'contained' }}
      />
    );
  }

  const pillarRows = distribution.filter((d) => d.entry_count > 0);
  const topPillar = pillarRows.length > 0 ? pillarRows[0] : null;
  const hasAnyData = !!summary && summary.total_entries > 0;

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={<LightbulbIcon sx={{ fontSize: 32, color: brandColors.warmGold }} />}
        title="LifeAdvice Overview"
        subtitle={`Advice members are recording, transforming, and sharing — last ${WINDOW_DAYS} days.`}
        helpId="lifeadvice-overview"
        actions={
          <Button startIcon={<RefreshIcon />} onClick={reload} disabled={loading}>
            Refresh
          </Button>
        }
      />

      {error && !down && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={reload}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <MetricCard
            title="Total entries"
            value={summary?.total_entries ?? 0}
            color="content"
            subtitle={`${summary?.entries_in_window ?? 0} in the last ${WINDOW_DAYS} days`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <MetricCard
            title="Active members"
            value={summary?.creators_in_window ?? 0}
            color="users"
            subtitle={`${summary?.total_creators ?? 0} all-time creators`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <MetricCard
            title="Total shares"
            value={summary?.total_shares ?? 0}
            color="revenue"
            subtitle={`${summary?.shared_entries ?? 0} entries shared`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <MetricCard
            title="Voice clones"
            value={summary?.voice_cloned_entries ?? 0}
            color="default"
            subtitle={`${summary?.ai_transformed_entries ?? 0} AI transformed`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <MetricCard
            title="Top pillar"
            value={topPillar ? topPillar.name : '—'}
            color="content"
            subtitle={topPillar ? `${topPillar.entry_count} entries` : 'No entries yet'}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <MetricCard
            title="Avg words"
            value={summary?.avg_word_count ?? 0}
            color="default"
            subtitle={`${summary?.draft_entries ?? 0} still in draft`}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper sx={{ p: 2.5, borderRadius: '14px', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
              Entries &amp; shares over time
            </Typography>
            {timeseries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No activity in this window yet.
              </Typography>
            ) : (
              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={timeseries} margin={{ top: 4, right: 8, bottom: 4, left: -16 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="day"
                      tickFormatter={(v) => formatDate(String(v))}
                      tick={{ fontSize: 11 }}
                      minTickGap={24}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <RTooltip labelFormatter={(v) => formatDate(String(v))} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line
                      type="monotone"
                      dataKey="entries_created"
                      name="Created"
                      stroke={brandColors.deepTeal}
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="entries_shared"
                      name="Shared"
                      stroke={brandColors.warmGold}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Paper sx={{ p: 2.5, borderRadius: '14px', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
              Entries by wisdom pillar
            </Typography>
            {pillarRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No entries in this window yet.
              </Typography>
            ) : (
              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pillarRows}
                      dataKey="entry_count"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {pillarRows.map((row) => (
                        <Cell key={row.category_id} fill={row.color || brandColors.slateGray} />
                      ))}
                    </Pie>
                    <RTooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </Paper>
        </Grid>
      </Grid>

      {!hasAnyData && !error && (
        <Box sx={{ mb: 3 }}>
          <EmptyState
            icon="empty_box"
            title="No member advice yet"
            description="Once members record their first entries they will appear here. The pillar, prompt, and curated library pages below are ready to configure in the meantime."
            action={{ label: 'Refresh', onClick: reload, variant: 'outlined' }}
          />
        </Box>
      )}

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
        Explore the section
      </Typography>
      <Grid container spacing={2}>
        {NAV_CARDS.map((card) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={card.path}>
            <Card sx={{ borderRadius: '14px', height: '100%' }}>
              <CardActionArea
                onClick={() => navigate(`/dashboard/lifeadvice/${card.path}`)}
                sx={{ height: '100%' }}
                aria-label={`Open ${card.label}`}
              >
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {card.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {card.blurb}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default LifeAdviceOverviewPage;
